import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { BuddyUser, BuddyUserMentee, BuddyUserMentor } from '@adornis/bh-matching-user/entities/buddy-user.js';
import type { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import { buildFullOptions, type DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { StringAspectConfig } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { html } from 'lit';
import { BuddyExportHiddenFields } from '../static/BuddyExportHiddenFields.js';
import { subscribeMatchedBuddyUsers } from './BuddyExportAPI.js';

export function buildBuddyExportBuildOptions(
  dataViewOptions: DataViewOptions<typeof BuddyUser>,
  targetTimeframe?: NarrowedEntity<Timeframe, { _id: 1 }>,
  onlyWithMatch = false,
) {
  const opt: DataViewOptions<typeof BuddyUser> = {
    ...{
      class: BuddyUser,
    },
    isColumnSelectable: true,
    view: new View<typeof BuddyUser>({
      filter: new AdornisFilter({
        searchFields: ['email'],
        aspects: [],
      }),
    }),
    customColumns: [
      { fieldName: 'email' },
      {
        fieldName: '_class',
        filterAspect: {
          ...StringAspectConfig('equals'),
          renderEditor: () => html`
            <x-dropdown-selection
              .selectables=${[BuddyUserMentee._class, BuddyUserMentor._class]}
            ></x-dropdown-selection>
          `,
        } as any,
      },
    ],
    fieldSelectionOptions: {
      shouldIncludeField: bqlFieldDef => !BuddyExportHiddenFields.includes(bqlFieldDef.name),
    },
    onViewChanged: () => null,
    emptyState: () => html`<tr-text>Keine Einträge</tr-text>`,

    ...dataViewOptions,
  };

  const timeframeAspect = (opt.view?.filter?.aspects ?? []).find(aspect => aspect.fieldName === 'timeframeID');

  if (targetTimeframe && (!timeframeAspect || (timeframeAspect as any).value !== targetTimeframe._id)) {
    const newAspect =
      timeframeAspect ??
      new (StringAspectConfig('equals').aspectConstructor as any)({
        mode: 'equals',
        value: null,
        fieldName: 'timeframeID',
      });

    newAspect.value = targetTimeframe._id;

    if (!timeframeAspect) {
      opt.view?.filter?.aspects.push(newAspect);
    }
  }

  if (onlyWithMatch) {
    opt.operation = subscribeMatchedBuddyUsers;
    const newAspect = new (StringAspectConfig('equals').aspectConstructor as any)({
      mode: 'equals',
      value: null,
      fieldName: '_class',
    });

    newAspect.value = dataViewOptions.class._class;
    opt.view?.filter?.aspects.push(newAspect);
  }

  return buildFullOptions(opt, {
    overridesForFieldName: { timeframeID: StringAspectConfig('equals') },
    fieldAspectGeneration: {
      shouldIncludeField: bqlFieldDef => !BuddyExportHiddenFields.includes(bqlFieldDef.name),
    },
  });
}
